import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";

import LetterCard from "./LetterCard";

const COLUMNS = [
  {
    id: "draft",
    title: "Brouillons",
    subtitle: null,
  },
  {
    id: "scheduled",
    title: "Programmées",
    subtitle: "envoi automatique après la mort.",
  },
  {
    id: "sent",
    title: "Envoyées",
    subtitle: "envoi immédiat.",
  },
];

/** Lock swipe axis after this much movement (px). */
const AXIS_LOCK_PX = 10;
/** Extra nudge past mid-column before snapping to the next page. */
const SWIPE_COMMIT_RATIO = 0.18;

const isDesktopBoard = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(min-width: 1024px)").matches;

const findColumnScroll = (target) =>
  target?.closest?.("[data-column-scroll]") || null;

function KanbanColumn({
  column,
  letters,
  onAddDraft,
  onDelete,
  onSendNow,
  columnRef,
}) {
  return (
    <section
      ref={columnRef}
      data-column-id={column.id}
      className="flex h-full w-[min(320px,calc(100vw-2.5rem))] shrink-0 snap-center flex-col rounded-2xl bg-dashboardColumn p-4 lg:h-auto lg:w-auto lg:min-w-0 lg:shrink lg:snap-align-none lg:p-5"
    >
      <div className="mb-4 shrink-0 border-b border-secondaryText/25 pb-3">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h2 className="font-josefin text-xs font-bold uppercase tracking-wider text-textMuted">
            {column.title}
          </h2>
          {column.id === "draft" && (
            <button
              type="button"
              onClick={onAddDraft}
              className="flex h-6 w-6 items-center justify-center rounded-md bg-brandPurple text-white transition hover:bg-primaryText"
              aria-label="Nouveau brouillon"
            >
              <Plus size={14} strokeWidth={2.5} />
            </button>
          )}
          {column.subtitle && (
            <p className="font-josefin text-xs italic text-textMuted">
              {column.subtitle}
            </p>
          )}
        </div>
      </div>
      {/* Vertical letter list — board JS owns touch (x = columns, y = list). */}
      <div
        data-column-scroll
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-y-contain lg:overflow-visible"
      >
        {letters.length === 0 && (
          <div className="rounded-xl bg-white/60 px-4 py-10 text-center font-josefin text-sm text-textMuted/70">
            Aucune lettre
          </div>
        )}
        {letters.map((letter) => (
          <LetterCard
            key={letter._id}
            letter={letter}
            column={column.id}
            onDelete={onDelete}
            onSendNow={onSendNow}
          />
        ))}
      </div>
    </section>
  );
}

function ColumnDots({ activeIndex, onSelect }) {
  return (
    <div
      className="mt-2 flex shrink-0 items-center justify-center gap-2.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-1 lg:hidden"
      role="tablist"
      aria-label="Colonnes du tableau"
    >
      {COLUMNS.map((column, index) => {
        const isActive = index === activeIndex;
        return (
          <button
            key={column.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={column.title}
            onClick={() => onSelect(index)}
            className={`h-2.5 rounded-full transition-all duration-200 ${
              isActive
                ? "w-7 bg-brandPurple"
                : "w-2.5 bg-secondaryText/55 hover:bg-secondaryText/75"
            }`}
          />
        );
      })}
    </div>
  );
}

function KanbanBoard({
  board,
  onAddDraft,
  onDelete,
  onSendNow,
}) {
  const scrollerRef = useRef(null);
  const columnRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const getClosestColumnIndex = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return 0;

    const scrollerCenter = scroller.scrollLeft + scroller.clientWidth / 2;
    let closest = 0;
    let closestDist = Infinity;

    columnRefs.current.forEach((el, index) => {
      if (!el) return;
      const center = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(center - scrollerCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closest = index;
      }
    });

    return closest;
  }, []);

  const scrollToColumn = useCallback((index) => {
    const el = columnRefs.current[index];
    const scroller = scrollerRef.current;
    if (!el || !scroller) return;

    // Scroll the board scroller only — avoid scrollIntoView moving the page.
    const left = el.offsetLeft - (scroller.clientWidth - el.offsetWidth) / 2;
    scroller.scrollTo({
      left: Math.max(0, left),
      behavior: "smooth",
    });
    setActiveIndex(index);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const updateActiveFromScroll = () => {
      if (isDesktopBoard()) return;
      setActiveIndex((prev) => {
        const closest = getClosestColumnIndex();
        return prev === closest ? prev : closest;
      });
    };

    updateActiveFromScroll();
    scroller.addEventListener("scroll", updateActiveFromScroll, {
      passive: true,
    });
    window.addEventListener("resize", updateActiveFromScroll);

    return () => {
      scroller.removeEventListener("scroll", updateActiveFromScroll);
      window.removeEventListener("resize", updateActiveFromScroll);
    };
  }, [getClosestColumnIndex]);

  // Mobile: own the gesture so nested letter lists cannot steal horizontal swipes
  // (iOS commits to vertical scroll unless preventDefault runs before that claim).
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const gesture = {
      tracking: false,
      axis: null,
      startX: 0,
      startY: 0,
      lastX: 0,
      startScrollLeft: 0,
      startScrollTop: 0,
      verticalEl: null,
      startIndex: 0,
    };

    const resetGesture = () => {
      gesture.tracking = false;
      gesture.axis = null;
      gesture.verticalEl = null;
      scroller.style.scrollSnapType = "";
    };

    const onTouchStart = (event) => {
      if (isDesktopBoard() || event.touches.length !== 1) return;
      // Ignore swipes that begin on controls (menus, buttons).
      if (event.target?.closest?.("button, a, input, textarea, select, [role='menu']")) {
        return;
      }

      const touch = event.touches[0];
      gesture.tracking = true;
      gesture.axis = null;
      gesture.startX = touch.clientX;
      gesture.startY = touch.clientY;
      gesture.lastX = touch.clientX;
      gesture.startScrollLeft = scroller.scrollLeft;
      gesture.verticalEl = findColumnScroll(event.target);
      gesture.startScrollTop = gesture.verticalEl?.scrollTop ?? 0;
      gesture.startIndex = getClosestColumnIndex();
      // Avoid CSS scroll-behavior:smooth fighting finger-driven scrollLeft.
      scroller.style.scrollBehavior = "auto";
    };

    const onTouchMove = (event) => {
      if (!gesture.tracking || isDesktopBoard() || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      const dx = touch.clientX - gesture.startX;
      const dy = touch.clientY - gesture.startY;
      gesture.lastX = touch.clientX;

      if (gesture.axis == null) {
        // Block native scroll immediately so iOS/Android cannot claim the
        // nested letter list before we know the axis.
        event.preventDefault();
        if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) {
          return;
        }
        gesture.axis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
        if (gesture.axis === "x") {
          scroller.style.scrollSnapType = "none";
        }
      }

      if (gesture.axis === "x") {
        event.preventDefault();
        scroller.scrollLeft = gesture.startScrollLeft - dx;
        return;
      }

      if (gesture.axis === "y" && gesture.verticalEl) {
        event.preventDefault();
        const maxTop =
          gesture.verticalEl.scrollHeight - gesture.verticalEl.clientHeight;
        const nextTop = gesture.startScrollTop - dy;
        gesture.verticalEl.scrollTop = Math.max(0, Math.min(maxTop, nextTop));
      }
    };

    const onTouchEnd = () => {
      if (!gesture.tracking) return;
      const wasHorizontal = gesture.axis === "x";
      const dx = gesture.lastX - gesture.startX;
      const startIndex = gesture.startIndex;
      resetGesture();
      scroller.style.scrollBehavior = "";
      if (!wasHorizontal || isDesktopBoard()) return;

      const commitPx = Math.max(48, scroller.clientWidth * SWIPE_COMMIT_RATIO);
      let target = getClosestColumnIndex();
      // Strong swipe: advance even if the finger didn't cross mid-column yet.
      if (Math.abs(dx) >= commitPx) {
        target = dx < 0
          ? Math.min(COLUMNS.length - 1, startIndex + 1)
          : Math.max(0, startIndex - 1);
      }
      scrollToColumn(target);
    };

    scroller.addEventListener("touchstart", onTouchStart, {
      passive: true,
      capture: true,
    });
    scroller.addEventListener("touchmove", onTouchMove, {
      passive: false,
      capture: true,
    });
    scroller.addEventListener("touchend", onTouchEnd, { capture: true });
    scroller.addEventListener("touchcancel", onTouchEnd, { capture: true });

    return () => {
      scroller.removeEventListener("touchstart", onTouchStart, true);
      scroller.removeEventListener("touchmove", onTouchMove, true);
      scroller.removeEventListener("touchend", onTouchEnd, true);
      scroller.removeEventListener("touchcancel", onTouchEnd, true);
      scroller.style.scrollSnapType = "";
      scroller.style.scrollBehavior = "";
    };
  }, [getClosestColumnIndex, scrollToColumn]);

  return (
    // Fill remaining main height on mobile so dots sit under columns with no
    // leftover white band. Desktop keeps natural grid height + page scroll.
    <div className="flex min-h-0 flex-1 flex-col lg:block lg:flex-none">
      <div
        ref={scrollerRef}
        className="-mx-1 flex min-h-0 flex-1 items-stretch gap-4 overflow-x-auto overflow-y-hidden overscroll-x-contain px-1 snap-x snap-mandatory touch-none max-lg:[&_*]:touch-none lg:mx-0 lg:h-auto lg:flex-none lg:grid lg:grid-cols-3 lg:gap-5 lg:overflow-x-visible lg:overflow-y-visible lg:px-0 lg:snap-none lg:touch-auto lg:[&_*]:touch-auto"
        style={{
          WebkitOverflowScrolling: "touch",
          scrollPaddingInline: "0.5rem",
          scrollBehavior: "auto",
        }}
      >
        {COLUMNS.map((column, index) => (
          <KanbanColumn
            key={column.id}
            column={column}
            letters={board[column.id] || []}
            onAddDraft={onAddDraft}
            onDelete={onDelete}
            onSendNow={onSendNow}
            columnRef={(el) => {
              columnRefs.current[index] = el;
            }}
          />
        ))}
      </div>

      <ColumnDots activeIndex={activeIndex} onSelect={scrollToColumn} />
    </div>
  );
}

export default KanbanBoard;
